<script lang="ts" setup>
import type { ToastMessageOptions } from 'primevue/toast'

interface Props {
  error?: boolean
  errorClass?: string
  errorMessage?: string | null
  hint?: string | null
  hintClass?: VueClassProp | null
  noHint?: boolean
  inline?: boolean
  label?: string | null
  labelClass?: VueClassProp | null
  labelErrorIndicator?: 'none' | 'color' | 'asterisk' | 'color-asterisk'
  loading?: boolean
  tooltipText?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  errorClass: 'p-invalid',
  labelErrorIndicator: 'color-asterisk',
  errorMessage: null,
  hint: null,
  hintClass: null,
  label: null,
  labelClass: null,
  tooltipText: null,
})
const error = toRef(() => props.error)
const toast = useToast()

const notification: ComputedRef<ToastMessageOptions> = computed(() => ({
  group: 'notifications',
  summary: 'Contigo',
  detail: {
    text: props.tooltipText,
    close: () => toast.remove(notification.value),
  },

  closable: false,
}))

const displayTooltipAsMessage = () => {
  toast.remove(notification.value)
  toast.add(notification.value)
}
provide('error', error)
</script>

<template>
  <div class="flex flex-col justify-between">
    <div
      :class="['flex w-full h-full', inline ? 'items-center' : 'flex-col justify-between', 'gap-1']"
    >
      <label
        v-if="label"
        :class="[
          {
            [errorClass]: error && errorClass && labelErrorIndicator.includes('color'),
            'flex items-center gap-1 text-sm md:text-base': true,
          },
          labelClass,
        ]"
      >
        <span class="font-bold min-h-3 md:min-h-6">{{ label }}</span>
        <span
          v-if="error && labelErrorIndicator.includes('asterisk')"
          :class="errorClass"
        >
          *
        </span>
        <template v-if="tooltipText">
          <span
            v-if="$viewport.isGreaterOrEquals('lg')"
            v-tooltip.top="tooltipText"
            class="icon-[ph--info-bold]"
          />
          <span
            v-else
            class="icon-[ph--info-bold] cursor-pointer"
            @click="displayTooltipAsMessage"
          />
        </template>
      </label>

      <PvProgressBar
        v-if="loading"
        mode="indeterminate"
        class="input-load my-[10px] self-stretch"
      />

      <slot v-else />
    </div>

    <InputHint
      v-if="!noHint"
      :hint="error ? errorMessage : hint"
      class="mt-1 text-xs"
      :class="[error ? errorClass : hintClass]"
    />
  </div>
</template>
